import { TokenManager } from '@tankef/api-client';

const AUTH_TERMS_GATE_ACCEPTED_KEY = 'auth_terms_gate_accepted';
const PERSON_TYPE_KEY = 'tkf.registration.personType';

export type AuthEntryFlow = 'login' | 'register';

/** Tipo de persona regulado por CNBV/SAT. */
export type PersonType = 'individual' | 'business';

export const DEFAULT_PERSON_TYPE: PersonType = 'individual';

export function getAuthEntryFlow(search: string): AuthEntryFlow {
  const params = new URLSearchParams(search);
  return params.get('flow') === 'register' ? 'register' : 'login';
}

/**
 * Lee `?type=individual|business` del query string. Default: `individual`.
 * Persona moral aún no se enruta pero el gate ya acepta el flag para
 * preparar el flujo dual sin breaking changes.
 */
export function getPersonTypeFromSearch(search: string): PersonType {
  const params = new URLSearchParams(search);
  const value = params.get('type');
  return value === 'business' ? 'business' : DEFAULT_PERSON_TYPE;
}

export function getPersistedPersonType(): PersonType {
  if (typeof window === 'undefined') return DEFAULT_PERSON_TYPE;
  const value = window.sessionStorage.getItem(PERSON_TYPE_KEY);
  return value === 'business' ? 'business' : DEFAULT_PERSON_TYPE;
}

export function setPersistedPersonType(type: PersonType): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(PERSON_TYPE_KEY, type);
  // sessionStorage no dispara 'storage' en la misma tab — emitimos un
  // evento custom para que usePersonType() reaccione de inmediato.
  window.dispatchEvent(new Event('tkf:personType:change'));
}

export function clearPersistedPersonType(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PERSON_TYPE_KEY);
  window.dispatchEvent(new Event('tkf:personType:change'));
}

/**
 * Fuerza Persona Física de forma SÍNCRONA y SIN emitir el evento de cambio.
 *
 * Pensado para el gate de Persona Moral: cuando PM está deshabilitado por
 * entorno navegamos de inmediato a `/onboarding`, así que limpiar cualquier
 * intención de PM previamente persistida (p.ej. vía `?type=business` en el gate
 * de T&C) debe ocurrir ANTES del `<Navigate>`. Hacerlo en `useEffect` correría
 * después de que `<Navigate>` ya montó la siguiente pantalla (condición de
 * carrera → fuga al flujo moral). Omitimos el evento porque no hay listeners
 * que refrescar en esta tab: navegamos fuera y la pantalla destino lee en
 * montaje.
 */
export function forcePersonTypeIndividualSync(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(PERSON_TYPE_KEY, DEFAULT_PERSON_TYPE);
}

export function hasAcceptedAuthTermsGate(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.sessionStorage.getItem(AUTH_TERMS_GATE_ACCEPTED_KEY) === 'true';
}

export function setAcceptedAuthTermsGate(accepted: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (accepted) {
    window.sessionStorage.setItem(AUTH_TERMS_GATE_ACCEPTED_KEY, 'true');
    return;
  }

  window.sessionStorage.removeItem(AUTH_TERMS_GATE_ACCEPTED_KEY);
}

export function clearAcceptedAuthTermsGate(): void {
  setAcceptedAuthTermsGate(false);
}

export function hasOnboardingDraftSession(): boolean {
  return Boolean(TokenManager.getOnboardingSessionId());
}
